import { xcss, Box, Stack } from '@atlaskit/primitives';
import { Main } from '@atlassian/navigation-system';
import React from 'react';
import { RouteComponent } from 'react-resource-router';

import { useHelp } from '@townsquare/navigation';
import { HomepageRoute, LayoutType } from '@townsquare/router/route-types';

import { GriffinResponsiveAside } from './GriffinResponsiveAside';

const MAIN_CONTENT_LX_WIDTH = '1232px';

type AsideType = Exclude<HomepageRoute['aside'], undefined>;
type NavigationType = Exclude<HomepageRoute['navigation'], undefined>;

type GriffinLayoutProps = {
  aside: AsideType | undefined;
  navigation: NavigationType | undefined;
  layout: LayoutType;
};

/**
 * Additional default styling to conform to a grid structure
 */
const defaultMainContentStyles = xcss({
  marginInlineEnd: 'auto',
  marginInlineStart: 'auto',
  maxWidth: MAIN_CONTENT_LX_WIDTH,
  paddingInlineStart: 'space.400',
  paddingInlineEnd: 'space.400',
});
const helpPanelStyles = xcss({ position: 'absolute', zIndex: 'navigation', insetInlineEnd: 'space.0' });

const GriffinMainContent = (props: GriffinLayoutProps) => {
  const Navigation = props.navigation;
  const useDefaultStyle = props.layout === 'home';
  const { isOpen: isHelpOpen } = useHelp();

  return (
    <Main>
      {isHelpOpen && (
        <>
          {/* Add anchor for help-panel to attach to */}
          <Box xcss={helpPanelStyles} id="help-panel" />
        </>
      )}
      {useDefaultStyle ? (
        <Box xcss={defaultMainContentStyles}>
          {Navigation ? (
            <Stack grow="fill">
              <Navigation />
              <RouteComponent />
            </Stack>
          ) : (
            <RouteComponent />
          )}
        </Box>
      ) : (
        <RouteComponent />
      )}
    </Main>
  );
};

const GriffinWithAside = (props: GriffinLayoutProps & { aside: AsideType }) => {
  const AsideComponent = props.aside;

  return (
    <>
      <GriffinMainContent {...props} />
      <GriffinResponsiveAside>
        <AsideComponent />
      </GriffinResponsiveAside>
    </>
  );
};

export const GriffinLayout = (props: GriffinLayoutProps) =>
  props.aside ? <GriffinWithAside {...props} aside={props.aside} /> : <GriffinMainContent {...props} />;
